import { useEffect, useRef, useState } from "react";
import { WEEKDAYS, WEEKDAYS_SHORT, hourRange } from "./statsData";

// Gráficos de la sección Estadísticas, hechos con SVG y CSS (sin librerías). Las animaciones
// de entrada son de CSS (.lp-st-*) y se apagan con prefers-reduced-motion. Las líneas aparecen
// de izquierda a derecha recortando el SVG (clip-path), que no depende del largo del trazo.

const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export const formatInt = (n) => Math.round(n).toLocaleString("es-AR");
export const formatMoney = (n) => "$" + Math.round(n).toLocaleString("es-AR");
export const formatPct = (n) => (n * 100).toLocaleString("es-AR", { maximumFractionDigits: n > 0 && n < 0.1 ? 1 : 0 }) + "%";

// Número que sube desde 0 (o desde el valor anterior) hasta `value`.
export const CountUp = ({ value, format = formatInt, duration = 900 }) => {
    const [shown, setShown] = useState(() => reducedMotion() ? value : 0);
    const from = useRef(shown);

    useEffect(() => {
        if (reducedMotion()) {
            setShown(value);
            return;
        }
        const start = performance.now();
        const begin = from.current;
        let frame;
        const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - t, 3);
            const current = begin + (value - begin) * eased;
            from.current = current;
            setShown(current);
            if (t < 1) frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [value, duration]);

    return <>{format(shown)}</>;
}

// Variación contra el período anterior. `invert` para cuando bajar es bueno (carritos abandonados).
export const Delta = ({ now, before, invert, label }) => {
    if (!before && !now) return <span className="lp-st-delta lp-st-delta-flat">Sin cambios</span>;
    if (!before) return <span className="lp-st-delta lp-st-delta-up">Nuevo</span>;
    const change = (now - before) / before;
    const flat = Math.abs(change) < 0.005;
    const good = invert ? change < 0 : change > 0;
    const pct = Math.abs(change * 100).toLocaleString("es-AR", { maximumFractionDigits: 0 });
    return (
        <span className={"lp-st-delta " + (flat ? "lp-st-delta-flat" : good ? "lp-st-delta-up" : "lp-st-delta-down")} title={label}>
            {!flat && <svg aria-hidden="true" width="10" height="10" viewBox="0 0 10 10"><path d={change > 0 ? "M5 1l4 6H1z" : "M5 9L1 3h8z"} fill="currentColor"></path></svg>}
            {flat ? "Igual" : `${pct}%`}
            <span className="lp-panel-sr">{flat ? " igual que " : change > 0 ? " más que " : " menos que "}{label}</span>
        </span>
    )
}

// Línea chiquita para las tarjetas de números.
export const Sparkline = ({ values }) => {
    const max = Math.max(1, ...values);
    const n = values.length;
    if (n < 2) return null;
    const pts = values.map((v, i) => `${(i / (n - 1)) * 100},${30 - (v / max) * 26 - 2}`);
    return (
        <svg className="lp-st-spark" viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden="true">
            <polygon points={`0,30 ${pts.join(" ")} 100,30`} className="lp-st-spark-area"></polygon>
            <polyline points={pts.join(" ")} className="lp-st-spark-line" vectorEffect="non-scaling-stroke"></polyline>
        </svg>
    )
}

const niceMax = (n) => {
    if (n <= 4) return 4;
    const pow = Math.pow(10, Math.floor(Math.log10(n)));
    const steps = [1, 2, 2.5, 5, 10];
    return steps.map((s) => s * pow).find((s) => s >= n) || n;
}

const dayShort = (date) => date.toLocaleDateString("es-AR", { day: "numeric", month: "short" }).replace(".", "");
const dayLong = (date) => date.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" });

// Visitas y pedidos por día: área con línea, tooltip al pasar el dedo o el mouse.
export const TrendChart = ({ days, series }) => {
    const [active, setActive] = useState(null);
    const box = useRef(null);
    const n = days.length;
    const max = niceMax(Math.max(1, ...days.flatMap((d) => series.map((s) => d[s.key]))));
    const x = (i) => n === 1 ? 50 : (i / (n - 1)) * 100;
    const y = (v) => 100 - (v / max) * 100;

    const pick = (e) => {
        const rect = box.current.getBoundingClientRect();
        const i = Math.round(((e.clientX - rect.left) / rect.width) * (n - 1));
        setActive(Math.max(0, Math.min(n - 1, i)));
    };

    const labelEvery = n <= 7 ? 1 : n <= 31 ? 7 : 15;
    const point = active !== null ? days[active] : null;

    return (
        <div className="lp-st-trend">
            <div className="lp-st-trend-axis" aria-hidden="true">
                <span>{formatInt(max)}</span><span>{formatInt(max / 2)}</span><span>0</span>
            </div>
            <div className="lp-st-trend-plot" ref={box} onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setActive(null)}
                role="img" aria-label={`Gráfico de ${series.map((s) => s.label.toLowerCase()).join(" y ")} por día`}>
                <div className="lp-st-trend-grid" aria-hidden="true"><i></i><i></i><i></i></div>
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                    <defs>
                        {series.map((s) => (
                            <linearGradient key={s.key} id={"lp-st-grad-" + s.key} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={s.color} stopOpacity="0.3"></stop>
                                <stop offset="100%" stopColor={s.color} stopOpacity="0"></stop>
                            </linearGradient>
                        ))}
                    </defs>
                    {series.map((s) => {
                        const pts = days.map((d, i) => `${x(i)},${y(d[s.key])}`).join(" ");
                        return (
                            <g key={s.key} className={"lp-st-series lp-st-series-" + s.key}>
                                <polygon className="lp-st-area" points={`0,100 ${pts} 100,100`} fill={`url(#lp-st-grad-${s.key})`}></polygon>
                                <polyline className="lp-st-line" points={pts} vectorEffect="non-scaling-stroke"></polyline>
                            </g>
                        );
                    })}
                </svg>
                {point &&
                    <>
                        <span className="lp-st-trend-cursor" style={{ left: `${x(active)}%` }} aria-hidden="true"></span>
                        {series.map((s) => (
                            <span key={s.key} className={"lp-st-trend-dot lp-st-series-" + s.key} style={{ left: `${x(active)}%`, top: `${y(point[s.key])}%` }} aria-hidden="true"></span>
                        ))}
                        <div className={"lp-st-tip" + (x(active) > 60 ? " lp-st-tip-left" : "")} style={{ left: `${x(active)}%` }}>
                            <strong>{dayLong(point.date)}</strong>
                            {series.map((s) => (
                                <span key={s.key}><i className={"lp-st-swatch lp-st-series-" + s.key}></i>{s.label}: <b>{formatInt(point[s.key])}</b></span>
                            ))}
                        </div>
                    </>
                }
            </div>
            <div className="lp-st-trend-x" aria-hidden="true">
                {days.map((d, i) => (
                    <span key={i} style={{ left: `${x(i)}%` }} className={i % labelEvery === (n - 1) % labelEvery ? "" : "lp-st-hide"}>
                        {n <= 7 ? WEEKDAYS_SHORT[(d.date.getDay() + 6) % 7] : dayShort(d.date)}
                    </span>
                ))}
            </div>
        </div>
    )
}

// Embudo: cada paso con su barra y el porcentaje que sigue al siguiente.
export const Funnel = ({ steps }) => {
    const top = Math.max(1, steps[0].n);
    return (
        <ol className="lp-st-funnel">
            {steps.map((s, i) => (
                <li key={s.label} style={{ "--i": i }}>
                    <div className="lp-st-funnel-head">
                        <span>{s.label}</span>
                        <strong>{formatInt(s.n)}</strong>
                    </div>
                    <div className="lp-st-funnel-track">
                        <span className="lp-st-funnel-fill" style={{ width: `${Math.max(s.n ? 2 : 0, (s.n / top) * 100)}%` }}></span>
                    </div>
                    {i > 0 && <span className="lp-st-funnel-rate">{steps[i - 1].n ? formatPct(Math.min(1, s.n / steps[i - 1].n)) : "0%"} del paso anterior</span>}
                </li>
            ))}
        </ol>
    )
}

// Barras horizontales con nombre y número, para rankings de productos y categorías.
export const RankBars = ({ items, unit, empty, limit = 5 }) => {
    if (!items.length) return <p className="lp-panel-muted lp-st-empty">{empty}</p>;
    const shown = items.slice(0, limit);
    const max = Math.max(1, shown[0].n);
    return (
        <ol className="lp-st-rank">
            {shown.map((r, i) => (
                <li key={r.key} style={{ "--i": i }}>
                    <span className="lp-st-rank-pos" aria-hidden="true">{i + 1}</span>
                    <div className="lp-st-rank-body">
                        <div className="lp-st-rank-head">
                            <span className="lp-st-rank-name">{r.label}</span>
                            <strong>{formatInt(r.n)} <span className="lp-panel-muted">{unit(r.n)}</span></strong>
                        </div>
                        <div className="lp-st-rank-track"><span style={{ width: `${(r.n / max) * 100}%` }}></span></div>
                    </div>
                </li>
            ))}
        </ol>
    )
}

// Grilla de días por horas, con más color donde hubo más movimiento.
export const Heatmap = ({ grid, label }) => {
    const max = Math.max(1, ...grid.flat());
    const [active, setActive] = useState(null);
    return (
        <div className="lp-st-heat-wrap">
            <div className="lp-st-heat-scroll">
                <div className="lp-st-heat" role="img" aria-label={label} onPointerLeave={() => setActive(null)}>
                    <span></span>
                    {Array.from({ length: 24 }, (_, h) => <span key={h} className="lp-st-heat-hour" aria-hidden="true">{h % 3 === 0 ? h : ""}</span>)}
                    {grid.map((row, r) => (
                        <div key={r} className="lp-st-heat-row" style={{ "--r": r }}>
                            <span className="lp-st-heat-day" aria-hidden="true">{WEEKDAYS_SHORT[r]}</span>
                            {row.map((n, h) => (
                                <span key={h} className="lp-st-heat-cell" style={{ "--a": n ? 0.15 + (n / max) * 0.85 : 0 }}
                                    onPointerEnter={() => setActive({ r, h, n })} onPointerDown={() => setActive({ r, h, n })}></span>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <p className="lp-st-heat-info" aria-live="polite">
                {active ? <><strong>{WEEKDAYS[active.r]} de {hourRange(active.h)}</strong>: {formatInt(active.n)}</> : "Tocá un cuadrito para ver el detalle."}
            </p>
        </div>
    )
}

// Torta con agujero para repartir un total en 2 o 3 partes.
export const Donut = ({ parts, center, caption }) => {
    const total = parts.reduce((n, p) => n + p.n, 0);
    let acc = 0;
    return (
        <div className="lp-st-donut-wrap">
            <div className="lp-st-donut">
                <svg viewBox="0 0 42 42" aria-hidden="true">
                    <circle cx="21" cy="21" r="15.9155" className="lp-st-donut-bg"></circle>
                    {total > 0 && parts.map((p, i) => {
                        const share = (p.n / total) * 100;
                        const el = (
                            <circle key={p.label} cx="21" cy="21" r="15.9155" className={"lp-st-donut-seg lp-st-tone-" + p.tone}
                                style={{ "--i": i }} strokeDasharray={`${share} ${100 - share}`} strokeDashoffset={25 - acc}></circle>
                        );
                        acc += share;
                        return el;
                    })}
                </svg>
                <div className="lp-st-donut-center">
                    <strong>{center}</strong>
                    <span>{caption}</span>
                </div>
            </div>
            <ul className="lp-st-legend">
                {parts.map((p) => (
                    <li key={p.label}>
                        <i className={"lp-st-swatch lp-st-tone-" + p.tone}></i>
                        <span>{p.label}</span>
                        <strong>{total ? formatPct(p.n / total) : "–"}</strong>
                        <span className="lp-panel-muted">{formatInt(p.n)}</span>
                    </li>
                ))}
            </ul>
        </div>
    )
}

// Barra partida en tramos (retiro / envío / sin elegir).
export const SplitBar = ({ parts }) => {
    const total = parts.reduce((n, p) => n + p.n, 0);
    return (
        <div className="lp-st-split-wrap">
            <div className="lp-st-split" aria-hidden="true">
                {total > 0 && parts.filter((p) => p.n).map((p, i) => (
                    <span key={p.label} className={"lp-st-tone-" + p.tone} style={{ width: `${(p.n / total) * 100}%`, "--i": i }}></span>
                ))}
            </div>
            <ul className="lp-st-legend lp-st-legend-row">
                {parts.map((p) => (
                    <li key={p.label}>
                        <i className={"lp-st-swatch lp-st-tone-" + p.tone}></i>
                        <span>{p.label}</span>
                        <strong>{total ? formatPct(p.n / total) : "–"}</strong>
                    </li>
                ))}
            </ul>
        </div>
    )
}
